import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Guid } from "guid-typescript";

import { BannerStatus } from "@plex/react-components";

import { validationMessages, IMessageId } from "infrastructure/localization";
import { INotification, notification } from "infrastructure/notification";
import { getScreenExtraReducers } from "ui/app";

import { TokenFormat } from "./MessageId.helpers";
import { getMessageIdInitialState, IMessageIdState, IMessageResponse, IToken, ITokenDescriptionValidationResult } from "./MessageId.state";

const initialState = getMessageIdInitialState();

export const messageFormat = createSlice({
  initialState: initialState,
  name: "messageFormat",
  reducers: {
    loadMessageData: (state: IMessageIdState, _: PayloadAction<string>): IMessageIdState => {
      return { ...state, isLoading: true };
    },
    loadMessageDataCompleted: (state: IMessageIdState, action: PayloadAction<{ response: IMessageResponse; messageTokens: IToken[] }>): IMessageIdState => {
      const message: IMessageResponse = action.payload.response;
      const messageTokens: IToken[] = action.payload.messageTokens;

      return {
        ...state,
        isLoading: false,
        messageId: message.messageId,
        text: message.text,
        currentStatus: message.status,
        version: message.version,
        tokens: message.tokens.map(token => ({ ...token, id: Guid.raw(), originalType: token.type, customToken: !messageTokens.some(mt => mt.name === token.name) })),
        textPreview: message.text
      };
    },
    loadMessageDataFailed: (state: IMessageIdState, action: PayloadAction<INotification>): IMessageIdState => {
      return { ...state, isLoading: false, notifications: [action.payload] };
    },
    changeMessageIdField: (state: IMessageIdState, action: PayloadAction<string>): IMessageIdState => {
      return { ...state, messageId: action.payload };
    },
    changeTextField: (state: IMessageIdState, action: PayloadAction<string>): IMessageIdState => {
      return { ...state, text: action.payload };
    },
    addMessageId: (state: IMessageIdState, _: PayloadAction<string>): IMessageIdState => {
      return { ...state, isLoading: true };
    },
    addMessageIdCompleted: (state: IMessageIdState, action: PayloadAction<{ redirectPath: string; notification: INotification }>): IMessageIdState => {
      return {
        ...state,
        isLoading: false,
        shouldRedirect: action.payload.redirectPath ? true : false,
        redirectPath: action.payload.redirectPath,
        notifications: [action.payload.notification]
      };
    },
    addMessageIdFailed: (state: IMessageIdState, action: PayloadAction<INotification>): IMessageIdState => {
      return { ...state, isLoading: false, notifications: [action.payload] };
    },
    updateMessageId: (state: IMessageIdState, _: PayloadAction<string>): IMessageIdState => {
      return { ...state, isLoading: true };
    },
    updateMessageIdCompleted: (state: IMessageIdState, action: PayloadAction<{ redirectPath: string; notification: INotification }>): IMessageIdState => {
      return {
        ...state,
        isLoading: false,
        shouldRedirect: action.payload.redirectPath ? true : false,
        redirectPath: action.payload.redirectPath,
        notifications: [action.payload.notification]
      };
    },
    updateMessageIdFailed: (state: IMessageIdState, action: PayloadAction<INotification>): IMessageIdState => {
      return { ...state, isLoading: false, notifications: [action.payload] };
    },
    approveMessage: (state: IMessageIdState, _: PayloadAction<string>): IMessageIdState => {
      return { ...state, isLoading: true };
    },
    obsoleteMessage: (state: IMessageIdState, _: PayloadAction<string>): IMessageIdState => {
      return { ...state, isLoading: true };
    },
    deprecateMessage: (state: IMessageIdState, _: PayloadAction<string>): IMessageIdState => {
      return { ...state, isLoading: true };
    },
    updateStatusCompleted: (state: IMessageIdState, action: PayloadAction<{ newStatus: string; notification: INotification }>): IMessageIdState => {
      return {
        ...state,
        isLoading: false,
        notifications: [action.payload.notification],
        currentStatus: action.payload.newStatus
      };
    },
    updateStatusFailed: (state: IMessageIdState, action: PayloadAction<INotification>): IMessageIdState => {
      return { ...state, isLoading: false, notifications: [action.payload] };
    },
    setMessageIdValidationError: (state: IMessageIdState, action: PayloadAction<string>): IMessageIdState => {
      return { ...state, messageIdValidationError: action.payload };
    },
    setTextValidationError: (state: IMessageIdState, action: PayloadAction<string>): IMessageIdState => {
      return { ...state, textValidationError: action.payload };
    },
    setTokensDescriptionValidationState: (state: IMessageIdState, action: PayloadAction<ITokenDescriptionValidationResult>): IMessageIdState => {
      return { ...state, descriptionsValidationErrors: action.payload };
    },
    addTokens: (state: IMessageIdState, action: PayloadAction<IToken[]>): IMessageIdState => {
      const tokensArray: IToken[] = [];

      action.payload.forEach(token => {
        const existingToken: IToken | undefined = state.tokens.find(t => t.name === token.name);
        const tokenType = Object.values(TokenFormat).some(v => v === token.type) ? token.type : TokenFormat.variable;

        const refreshedToken: IToken = {
          id: existingToken ? existingToken.id : token.id,
          name: token.name,
          originalType: tokenType,
          type: tokenType,
          description: existingToken ? existingToken.description : ""
        };

        if (existingToken) {
          refreshedToken.value = existingToken.originalType === refreshedToken.type ? existingToken.value : null;
          refreshedToken.displayValue = existingToken.originalType === refreshedToken.type ? existingToken.displayValue : null;
        } else {
          refreshedToken.value = null;
          refreshedToken.displayValue = null;
        }

        tokensArray.push(refreshedToken);
      });

      tokensArray.push(...state.tokens.filter(t => t.customToken));

      return { ...state, isLoading: false, tokens: tokensArray };
    },

    addCustomToken: (state: IMessageIdState, _: PayloadAction): IMessageIdState => {
      const customToken: IToken = {
        id: Guid.raw(),
        name: "",
        originalType: TokenFormat.variable,
        type: TokenFormat.variable,
        description: "",
        customToken: true
      };

      return { ...state, isLoading: false, tokens: [...state.tokens, customToken] };
    },

    removeCustomToken: (state: IMessageIdState, action: PayloadAction<string>): IMessageIdState => {
      const tokens: IToken[] = state.tokens.filter(t => t.id !== action.payload);
      return { ...state, isLoading: false, tokens };
    },

    changeTextPreview: (state: IMessageIdState, action: PayloadAction<string>): IMessageIdState => {
      return { ...state, textPreview: action.payload };
    },

    changeTokenType: (state: IMessageIdState, action: PayloadAction<{ type: string; index: number }>): IMessageIdState => {
      const tokensArray = [...state.tokens];
      tokensArray[action.payload.index] = {
        ...state.tokens[action.payload.index],
        type: action.payload.type,
        value: null,
        displayValue: null
      };

      return { ...state, tokens: tokensArray };
    },

    changeTokenPreviewValue: (state: IMessageIdState, action: PayloadAction<{ index: number; value: any; displayValue: any }>): IMessageIdState => {
      const tokensArray = [...state.tokens];
      tokensArray[action.payload.index] = {
        ...state.tokens[action.payload.index],
        value: action.payload.value,
        displayValue: action.payload.displayValue
      };

      return { ...state, tokens: tokensArray };
    },
    changeTokenDescription: (state: IMessageIdState, action: PayloadAction<{ index: number; description: string }>): IMessageIdState => {
      const tokensArray = [...state.tokens];
      tokensArray[action.payload.index] = {
        ...state.tokens[action.payload.index],
        description: action.payload.description
      };

      return { ...state, tokens: tokensArray };
    },
    changeCustomTokenName: (state: IMessageIdState, action: PayloadAction<{ index: number; name: string }>): IMessageIdState => {
      const tokensArray = [...state.tokens];
      tokensArray[action.payload.index] = {
        ...state.tokens[action.payload.index],
        name: action.payload.name
      };

      return { ...state, tokens: tokensArray };
    },
    cancelLinkClick: (state: IMessageIdState): IMessageIdState => {
      return { ...state, isLoading: true, shouldRedirect: true, redirectPath: "/messages" };
    },
    setValidationBanner: (state: IMessageIdState, action: PayloadAction<boolean>): IMessageIdState => {
      return { ...state, notifications: action.payload ? [notification.warning().useMessageId(validationMessages.error("summary"))] : [] };
    },
    setUnexpectedErrorBanner: (state: IMessageIdState, action: PayloadAction<IMessageId>): IMessageIdState => {
      return { ...state, notifications: [notification.error().useMessageId(action.payload)] };
    },
    clearErrorBanners: (state: IMessageIdState): IMessageIdState => {
      return { ...state, notifications: [...state.notifications.filter(n => n.type !== BannerStatus.error)] };
    }
  },
  extraReducers: getScreenExtraReducers<IMessageIdState>("notifications", initialState)
});
